import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import useFeatureCheck from "../../../customHooks/useFeatureCheck";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, DatePicker, Input, message, Popconfirm, Row, Select, Table } from "antd";
import { AllowGrnWithoutPo } from "../../../utils/PosSettingConstants";

const { TextArea } = Input;
import { DeleteFilled, SearchOutlined, CloseOutlined } from "@ant-design/icons";
import moment from "moment";
import { INPUT_SIZE } from "../../../common/ThemeConstants";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import { postRequest } from "../../../services/mainApp.service";
import { clampQtyRateValue } from "../../../utils/commonfunction";
import { GRN } from "../../../common/InventoryEnum";
import BasicFormComponentInvTransaction from "../../../components/formComponent/BasicFormComponentInvTransaction";
import ModalComponent from "../../../components/formComponent/ModalComponent";

const initialFormValues = {
  TransactionMasterId: null,
  TransactionNo: "",
  TransactionDate: moment().format("YYYY-MM-DD"),
  ReferenceNo: "",
  Description: "",
  TransactionTypeId: GRN,
  BranchId: null,
  DepartmentId: null,
  Gross: 0,
  Tax: 0,
  Discount: 0,
  Amount: 0,
  VendorId: null,
  // PurchaseOrderId: null,
  ParentTransactionMasterId: null,
  IsEnable: true,
  IsSubmit: false,
  TransactionDetails: [],
};

const CreateEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);
  const [tableLoading, setTableLoading] = useState(false);
  const isEditMode = !!id;
  const [formFields, setFormFields] = useState(initialFormValues);
  const [formLoading, setFormLoading] = useState(false);
  const [supportingTable, setSupportingTable] = useState({});
  const [transactionDetails, setTransactionDetails] = useState([]);
  const [selectedItemForAdd, setSelectedItemForAdd] = useState(null);
  const [addDetailQty, setAddDetailQty] = useState("");
  const [addDetailRate, setAddDetailRate] = useState("");
  const [addDetailTax, setAddDetailTax] = useState("");
  const [addDetailDiscount, setAddDetailDiscount] = useState("");
  const itemSelectRef = useRef(null);
  const [poModalVisible, setPoModalVisible] = useState(false);
  const [poSearchText, setPoSearchText] = useState("");
  const checkFeatureAuth = useFeatureCheck();

  useEffect(() => {
    fetchData();

    return () => {
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    const branches = supportingTable?.Branches || [];
    const departments = supportingTable?.Departments || [];
    setFormFields((prev) => {
      let next = { ...prev };
      if (branches.length === 1 && (prev.BranchId == null || prev.BranchId === "")) {
        next.BranchId = branches[0].Id;
      }
      if (departments.length === 1 && (prev.DepartmentId == null || prev.DepartmentId === "")) {
        next.DepartmentId = departments[0].Id;
      }
      return next;
    });
  }, [supportingTable?.Branches, supportingTable?.Departments]);

  // In edit mode, fetch purchase orders when both BranchId and VendorId are available
  useEffect(() => {
    if (isEditMode && formFields.BranchId && formFields.VendorId) {
      fetchPurchaseOrdersByVendor(formFields.VendorId);
    }
  }, [isEditMode, formFields.BranchId, formFields.VendorId]);

  const fetchTransactionRelatedData = async (id, dataType, options = {}) => {
    const { showErrorMessage = true, onClear, onSuccess } = options;

    // Clear data if id is not provided (allow null for GetPendingPurchaseOrders - search without vendor)
    const allowNullId = dataType === "GetPendingPurchaseOrders";
    if (!id && !allowNullId) {
      if (onClear) onClear();
      return;
    }

    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      Id: id,
      DataType: dataType,
      BranchId: formFields.BranchId || null,
      DepartmentId: formFields.DepartmentId || null,
    };

    try {
      const response = await postRequest("/Inv_TransactionRelatedData", payload, controller);

      if (response.error) {
        const errorMsg = response.errorMessage || `Error fetching ${dataType}`;
        console.error(`Error fetching ${dataType}:`, errorMsg);
        if (showErrorMessage) {
          message.error(errorMsg);
        }
        if (onClear) onClear();
        return;
      }

      if (response.data.response === false) {
        const errorMsg = response.data.DataSet?.Table?.errorMessage || `Error fetching ${dataType}`;
        console.error(`Error fetching ${dataType}:`, errorMsg);
        if (showErrorMessage) {
          message.error(errorMsg);
        }
        if (onClear) onClear();
        return;
      }

      const dataSet = response.data;
      const data = dataSet.Data || dataSet.DataSet?.Table || [];

      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      if (error.message !== "canceled") {
        console.error(`Error fetching ${dataType}:`, error);
        if (showErrorMessage) {
          message.error(`Failed to fetch ${dataType}`);
        }
      }
      if (onClear) onClear();
    }
  };

  const fetchPurchaseOrdersByVendor = async (vendorId) => {
    await fetchTransactionRelatedData(
      vendorId,
      "GetPendingPurchaseOrders",
      {
        showErrorMessage: false,
        onClear: () => {
          setSupportingTable((prev) => ({
            ...prev,
            PurchaseOrders: [],
          }));
        },
        onSuccess: (purchaseOrders) => {
          setSupportingTable((prev) => ({
            ...prev,
            PurchaseOrders: purchaseOrders,
          }));
        },
      }
    );
  };

  const fetchPurchaseOrderItems = async (purchaseOrderId) => {
    await fetchTransactionRelatedData(
      purchaseOrderId,
      "GetPurchaseOrderItems",
      {
        showErrorMessage: true,
        onClear: () => {
          setTransactionDetails([]);
        },
        onSuccess: (items) => {
          // Map the response to transaction details format
          const mappedDetails = items.map((item) => {
            const remainingQty = (item.QtyPU ?? 0) - (item.DeliveredQtyPU ?? 0);
            const poRate = item.RatePU ?? item.Rate ?? 0;

            // In create mode: set qty to remaining qty, rate from PO, tax and discount to 0
            return {
              ItemId: item.ItemId ?? item.Id,
              ItemName: item.ItemName ?? item.Name ?? "",
              PurchaseUnitId: item.PurchaseUnitId ?? item.UnitId ?? null,
              PurchaseUnit: item.PurchaseUnit ?? item.UnitName ?? "",
              QtyPU: remainingQty,
              RatePU: poRate,
              TaxPU: 0,
              DiscountPU: 0,
              IssuanceUnitId: item.IssuanceUnitId ?? null,
              IssuanceUnit: item.IssuanceUnit ?? "",
              QtyIU: item.QtyIU ?? 0,
              RateIU: item.RateIU ?? 0,
              RecipeUnitId: item.RecipeUnitId ?? null,
              QtyRU: item.QtyRU ?? 0,
              RateRU: item.RateRU ?? 0,
              IsEnable: item.IsEnable !== false,
              DeliveredQtyPU: item.DeliveredQtyPU ?? 0,
              DeliveredQtyIU: item.DeliveredQtyIU ?? 0,
              DeliveredQtyRU: item.DeliveredQtyRU ?? 0,
              PORemainingQtyPU: remainingQty,
            };
          });

          setTransactionDetails(mappedDetails);
        },
      }
    );
  };

  const fetchData = async () => {
    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: GRN,
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      IsDetailData: isEditMode ? true : false,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching data"
        );
        return;
      }
      const dataSet = response.data;

      if (isEditMode) {
        const masterData = dataSet?.Data?.[0] || {};
        setFormFields((prev) => ({ ...prev, ...masterData }));

        const detailsData = masterData?.TransactionDetails || [];
        setTransactionDetails(detailsData);
      }

      // Set supporting tables if available
      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable({
          Branches: dataSet.AdditionalData?.Branches || dataSet.DataSet?.Table1 || [],
          Departments: dataSet.AdditionalData?.Departments || dataSet.DataSet?.Table2 || [],
          Vendors: dataSet.AdditionalData?.Vendors || [],
          TransactionNo: dataSet?.AdditionalData?.TransactionCode?.[0]?.Code || "",
          Items: dataSet.AdditionalData?.Items || dataSet.DataSet?.Table3 || dataSet.Data || [],
          PurchaseOrders: supportingTable?.PurchaseOrders || [],
        });
      }
    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching transactions:", error);
        message.error("Failed to fetch transactions");
      }
    }
  };

  const handleFormChange = (data) => {
    setFormFields((prev) => {
      const updated = { ...prev, [data.name]: data.value };

      // When Vendor is selected or changed, fetch Purchase Orders for that vendor
      if (data.name === "VendorId") {
        if (data.value) {
          fetchPurchaseOrdersByVendor(data.value);
        } else {
          // Clear purchase orders when vendor is cleared
          setSupportingTable((prev) => ({
            ...prev,
            PurchaseOrders: [],
          }));
        }
        // Clear Purchase Order selection when vendor changes
        updated.PurchaseOrderId = null;
        // Clear transaction details when vendor changes
        setTransactionDetails([]);
      }

      // When Purchase Order is selected or changed, fetch Purchase Order Items
      // In edit mode, don't fetch PO items as they're already loaded from saved data
      if (data.name === "ParentTransactionMasterId") {
        if (data.value) {
          if (!isEditMode) {
            fetchPurchaseOrderItems(data.value);
          }
          // In edit mode, transaction details are already loaded from saved data
        } else {
          // Clear transaction details when purchase order is cleared
          setTransactionDetails([]);
        }
      }

      return updated;
    });
  };

  const handleDateChange = (date) => {
    setFormFields((prev) => ({
      ...prev,
      TransactionDate: date ? date.format("YYYY-MM-DD") : null,
    }));
  };

  const itemList = supportingTable?.Items || [];
  const vendors = supportingTable?.Vendors || [];
  const purchaseOrders = supportingTable?.PurchaseOrders || [];

  const handleAddDetail = () => {
    const qty = clampQtyRateValue(addDetailQty);
    const rate = clampQtyRateValue(addDetailRate);
    const tax = parseFloat(addDetailTax) || 0;
    const discount = parseFloat(addDetailDiscount) || 0;

    if (selectedItemForAdd) {
      const itemId = selectedItemForAdd.ItemId ?? selectedItemForAdd.Id;
      const isDuplicate = transactionDetails.some(
        (d) => (d.ItemId ?? d.Id) != null && (d.ItemId ?? d.Id) === itemId
      );
      if (isDuplicate) {
        message.error("This item is already added to the table. Duplicate items are not allowed.");
        return;
      }

      const newDetail = {
        ItemId: itemId,
        ItemName: selectedItemForAdd.ItemName ?? selectedItemForAdd.Name,
        PurchaseUnitId: selectedItemForAdd.PurchaseUnitId ?? selectedItemForAdd.UnitId ?? null,
        PurchaseUnit: selectedItemForAdd.PurchaseUnit ?? selectedItemForAdd.UnitName ?? "",
        QtyPU: qty,
        RatePU: rate,
        TaxPU: tax,
        DiscountPU: discount,
        IssuanceUnitId: selectedItemForAdd.IssuanceUnitId ?? null,
        IssuanceUnit: selectedItemForAdd.IssuanceUnit ?? "",
        QtyIU: 0,
        RateIU: 0,
        RecipeUnitId: null,
        QtyRU: 0,
        RateRU: 0,
        IsEnable: true,
      };
      setTransactionDetails([newDetail, ...transactionDetails]);
      setSelectedItemForAdd(null);
      setAddDetailQty("");
      setAddDetailRate("");
      setAddDetailTax("");
      setAddDetailDiscount("");
      setTimeout(() => itemSelectRef.current?.focus(), 0);
    }
  };

  const handleDetailChange = (index, field, value) => {
    if (field === "QtyPU" || field === "RatePU") {
      value = clampQtyRateValue(value);
    }
    const updatedDetails = [...transactionDetails];
    const currentDetail = updatedDetails[index];

    // Validate QtyPU against PO Remaining Qty (only for lines loaded from a PO)
    if (field === "QtyPU") {
      const qtyValue = value;
      const remainingQty = currentDetail?.PORemainingQtyPU;
      if (remainingQty != null && qtyValue > remainingQty) {
        message.error(
          `Quantity cannot exceed PO Remaining Quantity (${remainingQty}). Please enter a value less than or equal to ${remainingQty}.`
        );
        return;
      }
    }

    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value,
    };

    if (field === "QtyPU" || field === "RatePU") {
      updatedDetails[index].QtyPU = updatedDetails[index].QtyPU || 0;
      updatedDetails[index].RatePU = updatedDetails[index].RatePU || 0;
    }
    if (field === "TaxPU" || field === "DiscountPU") {
      updatedDetails[index].TaxPU = updatedDetails[index].TaxPU ?? 0;
      updatedDetails[index].DiscountPU = updatedDetails[index].DiscountPU ?? 0;
    }

    setTransactionDetails(updatedDetails);
  };

  const handleRemoveDetail = (index) => {
    const updatedDetails = transactionDetails.filter((_, i) => i !== index);
    setTransactionDetails(updatedDetails);
  };

  const handleClearPurchaseOrder = async (record) => {
    const transactionMasterId = record.Id ?? record.id ?? record.TransactionMasterId;
    if (!transactionMasterId) return;

    const payload = {
      OperationId: 5,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionMasterId: transactionMasterId,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);

      if (response.error) {
        message.error(response.errorMessage || "Error clearing purchase order");
        return;
      }

      if (response.data?.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error clearing purchase order"
        );
        return;
      }

      message.success(response.successMessage || "Purchase order cleared successfully");
      handleFormChange({ name: "ParentTransactionMasterId", value: null });
      setTransactionDetails([]);
      setPoModalVisible(false);
      setPoSearchText("");
      fetchPurchaseOrdersByVendor(formFields.VendorId || null);
    } catch (error) {
      if (error.message !== "canceled") {
        console.error("Error clearing purchase order:", error);
        message.error("Failed to clear purchase order");
      }
    }
  };

  const calculateTotals = () => {
    let gross = 0;
    let taxTotal = 0;
    let discountTotal = 0;
    transactionDetails.forEach((detail) => {
      const lineAmount = (detail.QtyPU || 0) * (detail.RatePU || 0);
      const tax = parseFloat(detail.TaxPU) || 0;
      const discount = parseFloat(detail.DiscountPU) || 0;
      gross += lineAmount;
      taxTotal += tax;
      discountTotal += discount;
    });
    const netAmount = gross + taxTotal - discountTotal;

    setFormFields((prev) => ({
      ...prev,
      Gross: gross,
      Tax: taxTotal,
      Discount: discountTotal,
      Amount: netAmount,
      TotalAmount: netAmount,
    }));
  };

  useEffect(() => {
    calculateTotals();
  }, [transactionDetails, formFields.Tax, formFields.Discount]);

  const handleFormSave = async (e, operationId, isSubmitFlag) => {
    e.preventDefault();

    // Check POST feature permission when submitting
    if (isSubmitFlag && !checkFeatureAuth("POST", "GoodRecieving")) {
      return;
    }

    if (!formFields.VendorId) {
      message.error("Please select a vendor");
      return;
    }

    if (!formFields.BranchId) {
      message.error("Please select a branch");
      return;
    }

    if (!formFields.DepartmentId) {
      message.error("Please select a department");
      return;
    }

    if (!formFields.ParentTransactionMasterId) {
      message.error("Please select a purchase order");
      return;
    }

    if (transactionDetails.length === 0) {
      message.error("Please add at least one transaction detail");
      return;
    }

    // Validate QtyPU against PO remaining only for lines that came from a PO
    const invalidItems = transactionDetails.filter(
      (detail) =>
        detail.PORemainingQtyPU != null &&
        (detail.QtyPU || 0) > detail.PORemainingQtyPU
    );

    if (invalidItems.length > 0) {
      const invalidItemNames = invalidItems
        .map((item) => item.ItemName || "Unknown Item")
        .join(", ");
      message.error(
        `Quantity exceeds PO Remaining Quantity for the following items: ${invalidItemNames}. Please adjust the quantities before saving.`
      );
      return;
    }

    setFormLoading(true);

    const payload = {
      OperationId: operationId,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      TransactionNo: formFields.TransactionNo,
      TransactionDate: formFields.TransactionDate,
      ReferenceNo: formFields.ReferenceNo,
      Description: formFields.Description ?? "",
      TransactionTypeId: GRN,
      BranchId: formFields.BranchId,
      DepartmentId: formFields.DepartmentId,
      Gross: formFields.Gross || 0,
      Tax: formFields.Tax || 0,
      Discount: formFields.Discount || 0,
      Amount: formFields.Amount || 0,
      VendorId: formFields.VendorId,
      ParentTransactionMasterId: formFields.ParentTransactionMasterId || null,
      IsEnable: formFields.IsEnable,
      IsSubmit: isSubmitFlag || formFields.IsSubmit,
      TransactionDetails: transactionDetails.map((detail) => ({
        ItemId: detail.ItemId,
        PurchaseUnitId: detail.PurchaseUnitId || null,
        QtyPU: detail.QtyPU || 0,
        RatePU: detail.RatePU || 0,
        TaxPU: detail.TaxPU || 0,
        DiscountPU: detail.DiscountPU || 0,
        IssuanceUnitId: detail.IssuanceUnitId || null,
        QtyIU: 0,
        RateIU: 0,
        RecipeUnitId: detail.RecipeUnitId || null,
        QtyRU: 0,
        RateRU: 0,
        IsEnable: detail.IsEnable !== false,
      })),
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setFormLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage ||
          `Error ${isEditMode ? "updating" : "creating"} transaction`
        );
        return;
      }

      message.success(
        response.successMessage ||
        `Transaction ${isEditMode ? "updated" : "created"} successfully`
      );
      navigate("/goodRecieving");
    } catch (error) {
      setFormLoading(false);
      if (error.message !== "canceled") {
        console.error("Error saving transaction:", error);
        const apiError =
          error.response?.data?.Error_Message ||
          error.response?.data?.Message ||
          error.response?.data?.errorMessage ||
          error.response?.data?.ResponseMessage;
        message.error(
          apiError || `Failed to ${isEditMode ? "update" : "create"} good receiving`
        );
      }
    }
  };

  const detailColumns = useMemo(
    () => [
      { title: "Item Name", dataIndex: "ItemName", key: "ItemName" },
      { title: "Purchase Unit", dataIndex: "PurchaseUnit", key: "PurchaseUnit" },
      {
        title: "PO Remaining Qty",
        dataIndex: "PORemainingQtyPU",
        key: "PORemainingQtyPU",
        width: 120,
        align: "right",
        render: (value) => {
          const qty = parseFloat(value) || 0;
          return qty.toFixed(2);
        },
      },
      {
        title: "Qty",
        dataIndex: "QtyPU",
        key: "QtyPU",
        width: 100,
        render: (_, record, index) => (
          <Input
            type="number"
            min={0}
            value={record.QtyPU}
            onChange={(e) =>
              handleDetailChange(index, "QtyPU", parseFloat(e.target.value) || 0)
            }
            style={{ width: "100%" }}
            readOnly={formFields.IsSubmit}
            max={record.PORemainingQtyPU != null ? record.PORemainingQtyPU : undefined}
          />
        ),
      },
      {
        title: "Rate",
        dataIndex: "RatePU",
        key: "RatePU",
        width: 100,
        render: (_, record, index) => (
          <Input
            type="number"
            min={0}
            value={record.RatePU}
            onChange={(e) =>
              handleDetailChange(index, "RatePU", parseFloat(e.target.value) || 0)
            }
            style={{ width: "100%" }}
            readOnly={formFields.IsSubmit}
          />
        ),
      },
      {
        title: "Amount",
        key: "Amount",
        width: 120,
        align: "right",
        render: (_, record) => {
          const qty = record.QtyPU ?? 0;
          const rate = record.RatePU ?? 0;
          const amount = (parseFloat(qty) || 0) * (parseFloat(rate) || 0);
          return amount.toFixed(2);
        },
      },
      {
        title: "Tax",
        dataIndex: "TaxPU",
        key: "TaxPU",
        width: 100,
        render: (_, record, index) => (
          <Input
            type="number"
            value={record.TaxPU ?? ""}
            onChange={(e) =>
              handleDetailChange(index, "TaxPU", parseFloat(e.target.value) || 0)
            }
            style={{ width: "100%" }}
            readOnly={formFields.IsSubmit}
          />
        ),
      },
      {
        title: "Discount",
        dataIndex: "DiscountPU",
        key: "DiscountPU",
        width: 100,
        render: (_, record, index) => (
          <Input
            type="number"
            value={record.DiscountPU ?? ""}
            onChange={(e) =>
              handleDetailChange(index, "DiscountPU", parseFloat(e.target.value) || 0)
            }
            style={{ width: "100%" }}
            readOnly={formFields.IsSubmit}
          />
        ),
      },
      {
        title: "Net Amount",
        key: "NetAmount",
        width: 120,
        align: "right",
        render: (_, record) => {
          const qty = record.QtyPU ?? 0;
          const rate = record.RatePU ?? 0;
          const amount = (parseFloat(qty) || 0) * (parseFloat(rate) || 0);
          const tax = parseFloat(record.TaxPU) || 0;
          const discount = parseFloat(record.DiscountPU) || 0;
          const netAmount = amount + tax - discount;
          return netAmount.toFixed(2);
        },
      },
      ...(!formFields.IsSubmit
        ? [
          {
            title: "Action",
            key: "actions",
            width: 100,
            render: (_, record, index) => (
              <Button
                type="link"
                danger
                icon={<DeleteFilled />}
                onClick={() => handleRemoveDetail(index)}
              />
            ),
          },
        ]
        : []),
    ],
    [formFields.IsSubmit, handleDetailChange, handleRemoveDetail]
  );

  const formPanel = (
    <Fragment>
      <FormTextField
        colSpan={4}
        label="Transaction No"
        name="TransactionNo"
        size={INPUT_SIZE}
        value={isEditMode ? formFields.TransactionNo : supportingTable?.TransactionNo}
        readOnly={true}
      />

      <Col span={4}>
        <label style={{ display: "block", marginBottom: 5 }}>
          Transaction Date <span style={{ color: "red" }}>*</span>
        </label>
        {!formFields.IsSubmit ? (
          <>
            <DatePicker
              style={{ width: "100%" }}
              value={formFields.TransactionDate ? moment(formFields.TransactionDate) : null}
              onChange={handleDateChange}
              format="YYYY-MM-DD"
            />
          </>
        ) : (
          <>
            <FormTextField
              name="TransactionDate"
              size={INPUT_SIZE}
              value={formFields.TransactionDate ? moment(formFields.TransactionDate).format("YYYY-MM-DD") : null}
              readOnly={true}
            />
          </>
        )}
      </Col>


      <FormSelect
        colSpan={4}
        listItem={supportingTable.Branches || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="BranchId"
        label="Branch"
        value={formFields.BranchId}
        onChange={handleFormChange}
        required={true}
        disabled={formFields.IsSubmit}
      />

      <FormSelect
        colSpan={4}
        listItem={supportingTable.Departments || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="DepartmentId"
        label="Department"
        value={formFields.DepartmentId}
        onChange={handleFormChange}
        required={true}
        disabled={formFields.IsSubmit}
      />


      <FormSelect
        colSpan={4}
        listItem={vendors}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="VendorId"
        label="Vendor"
        value={formFields.VendorId}
        onChange={handleFormChange}
        required={true}
        disabled={formFields.IsSubmit}
        allowClear={false}
      />

      <Col span={4}>
        <Row gutter={8} align="bottom">
          <Col span={20}>
            <FormSelect
              colSpan={24}
              listItem={purchaseOrders}
              idName="Id"
              valueName="Name"
              size={INPUT_SIZE}
              name="ParentTransactionMasterId"
              label="Purchase Order"
              value={formFields.ParentTransactionMasterId}
              onChange={handleFormChange}
              required={AllowGrnWithoutPo ? false : true}
              disabled={formFields.IsSubmit || !formFields.VendorId}
              allowClear={false}
              placeholder={!formFields.VendorId ? "Select vendor first" : "Select purchase order"}
            />
          </Col>
          <Col span={4}>
            <Button
              type="link"
              onClick={() => {
                fetchPurchaseOrdersByVendor(formFields.VendorId || null);
                setPoModalVisible(true);
              }}
              disabled={formFields.IsSubmit}
              style={{ padding: "4px 0", marginBottom: 8 }}
            >
              Search
            </Button>
          </Col>
        </Row>
      </Col>

      <ModalComponent
        title="Select Purchase Order"
        isModalVisible={poModalVisible}
        handleCancel={() => {
          setPoModalVisible(false);
          setPoSearchText("");
        }}
        footer={null}
        width={700}
        closable={true}
      >
        <Input
          placeholder="Search by date, transaction no, or vendor..."
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          allowClear
          value={poSearchText}
          onChange={(e) => setPoSearchText(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <Table
          dataSource={
            (supportingTable?.PurchaseOrders || []).filter((record) => {
              if (!poSearchText.trim()) return true;
              const search = poSearchText.toLowerCase().trim();
              const date = (record.Date ?? "").toString().toLowerCase();
              const name = (record.Name ?? "").toString().toLowerCase();
              const vendor = (record.VendorName ?? "").toString().toLowerCase();
              return (
                date.includes(search) ||
                name.includes(search) ||
                vendor.includes(search)
              );
            })
          }
          rowKey={(record) => record.Id ?? record.id ?? record.TransactionMasterId ?? Math.random()}
          pagination={{ pageSize: 10 }}
          size="small"
          onRow={(record) => ({
            onClick: (e) => {
              if (e.target.closest("button")) return;
              const poId = record.Id ?? record.id ?? record.TransactionMasterId;
              if (poId) {
                const vendorId = record.VendorId ?? vendors.find((v) => v.Name === record.VendorName)?.Id ?? null;
                if (vendorId) {
                  setFormFields((prev) => ({
                    ...prev,
                    VendorId: vendorId,
                    ParentTransactionMasterId: poId,
                  }));
                } else {
                  handleFormChange({ name: "ParentTransactionMasterId", value: poId });
                }
                if (!isEditMode) {
                  fetchPurchaseOrderItems(poId);
                }
                setPoModalVisible(false);
                setPoSearchText("");
              }
            },
            style: { cursor: "pointer" },
          })}
          columns={[
            { title: "Date", dataIndex: "Date", key: "Date", width: 120 },
            { title: "Transaction No", dataIndex: "Name", key: "Name", width: 150 },
            { title: "Vendor", dataIndex: "VendorName", key: "VendorName" },
            {
              title: "Action",
              key: "action",
              width: 80,
              render: (_, record) => (
                <Popconfirm
                  title="Clear purchase order"
                  description="Are you sure you want to clear this purchase order?"
                  onConfirm={() => handleClearPurchaseOrder(record)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="link"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={(e) => e.stopPropagation()}
                    title="Clear"
                  />
                </Popconfirm>
              ),
            },
          ]}
        />
      </ModalComponent>

      <Col span={8}>
        <label style={{ display: "block", marginBottom: 5 }}>Description</label>
        <TextArea
          name="Description"
          value={formFields.Description ?? ""}
          onChange={(e) => handleFormChange({ name: "Description", value: e.target.value })}
          rows={1}
          placeholder="Enter description"
          readOnly={formFields.IsSubmit}
          disabled={formFields.IsSubmit}
          style={{ width: "100%" }}
        />
      </Col>

      <FormTextField
        colSpan={4}
        label="Gross Amount"
        name="Gross"
        size={INPUT_SIZE}
        value={formFields.Gross}
        type="text"
        isNumber="true"
        onChange={handleFormChange}
        readOnly={true}
      />
      <FormTextField
        colSpan={4}
        label="Tax"
        name="Tax"
        size={INPUT_SIZE}
        value={formFields.Tax}
        type="text"
        isNumber="true"
        onChange={handleFormChange}
        readOnly={true}
      />
      <FormTextField
        colSpan={4}
        label="Discount"
        name="Discount"
        size={INPUT_SIZE}
        value={formFields.Discount}
        type="text"
        isNumber="true"
        onChange={handleFormChange}
        readOnly={true}
      />
      <FormTextField
        colSpan={4}
        label="Net Amount"
        name="TotalAmount"
        size={INPUT_SIZE}
        value={formFields.TotalAmount}
        type="text"
        isNumber="true"
        onChange={handleFormChange}
        readOnly={true}
      />
      <FormTextField
        colSpan={4}
        label="Total Items"
        name="TotalItems"
        size={INPUT_SIZE}
        value={transactionDetails.length}
        readOnly={true}
        type="text"
        isNumber="true"
      />
    </Fragment>
  );

  const getItemId = (item) => item?.ItemId ?? item?.Id;
  const getItemName = (item) => (item?.ItemName ?? item?.Name ?? "") + " ( " + (item?.PurchaseUnit ?? item?.IssuanceUnit ?? item?.UnitName ?? "") + " ) ";
  const getItemCode = (item) => item?.ItemCode ?? item?.Code ?? "";
  const getBarcode = (item) => item?.Barcode ?? item?.BarCode ?? "";

  const addDetailEntryPanel = (
    <>
      {!formFields.IsSubmit && (
        <Row gutter={16} style={{ flex: 1, minWidth: 0 }}>
          <Col flex="160px">
            <label style={{ display: "block", marginBottom: 4 }}>Bar Code</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Search by Bar Code"
              showSearch
              allowClear
              value={selectedItemForAdd ? getBarcode(selectedItemForAdd) : undefined}
              onChange={(value) => {
                const item = itemList.find((i) => getBarcode(i) === value);
                setSelectedItemForAdd(item || null);
              }}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={[
                ...itemList
                  .filter((i) => getBarcode(i))
                  .map((item) => ({
                    value: getBarcode(item),
                    label: getBarcode(item),
                  })),
              ]}
            />
          </Col>
          <Col flex="160px">
            <label style={{ display: "block", marginBottom: 4 }}>Item Code</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Search by Item Code"
              showSearch
              allowClear
              value={selectedItemForAdd ? getItemCode(selectedItemForAdd) : undefined}
              onChange={(value) => {
                const item = itemList.find((i) => getItemCode(i) === value);
                setSelectedItemForAdd(item || null);
              }}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={[
                ...itemList
                  .filter((i) => getItemCode(i))
                  .map((item) => ({
                    value: getItemCode(item),
                    label: getItemCode(item),
                  })),
              ]}
            />
          </Col>
          <Col flex="200px">
            <label style={{ display: "block", marginBottom: 4 }}>Item</label>
            <Select
              ref={itemSelectRef}
              style={{ width: "100%" }}
              placeholder="Search by Item Name"
              showSearch
              allowClear
              value={selectedItemForAdd ? getItemId(selectedItemForAdd) : undefined}
              onChange={(value) => {
                const item = itemList.find((i) => getItemId(i) === value);
                setSelectedItemForAdd(item || null);
              }}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={[
                ...itemList.map((item) => ({
                  value: getItemId(item),
                  label: getItemName(item),
                })),
              ]}
            />
          </Col>
          <Col flex="120px">
            <label style={{ display: "block", marginBottom: 4 }}>Qty</label>
            <Input
              type="number"
              placeholder="Qty"
              value={addDetailQty}
              onChange={(e) => setAddDetailQty(e.target.value)}
              min={0}
              step={1}
            />
          </Col>
          <Col flex="120px">
            <label style={{ display: "block", marginBottom: 4 }}>Rate</label>
            <Input
              type="number"
              placeholder="Rate"
              value={addDetailRate}
              onChange={(e) => setAddDetailRate(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleAddDetail();
                }
              }}
              min={0}
              step={1}
            />
          </Col>
          <Col flex="120px">
            <label style={{ display: "block", marginBottom: 4 }}>Tax</label>
            <Input
              type="number"
              placeholder="Tax"
              value={addDetailTax}
              onChange={(e) => setAddDetailTax(e.target.value)}
              min={0}
              step={0.01}
            />
          </Col>
          <Col flex="120px">
            <label style={{ display: "block", marginBottom: 4 }}>Discount</label>
            <Input
              type="number"
              placeholder="Discount"
              value={addDetailDiscount}
              onChange={(e) => setAddDetailDiscount(e.target.value)}
              min={0}
              step={0.01}
            />
          </Col>
          <Col flex="120px">
            <label style={{ display: "block", marginBottom: 4 }}>Net Amount</label>
            <Input
              type="number"
              placeholder="Net Amount"
              value={((parseFloat(addDetailQty) || 0) * (parseFloat(addDetailRate) || 0) + (parseFloat(addDetailTax) || 0) - (parseFloat(addDetailDiscount) || 0)).toFixed(2)}
              min={0}
              step={0.01}
              disabled={true}
            />
          </Col>
        </Row>
      )}
    </>
  );

  return (
    <BasicFormComponentInvTransaction
      pageMode="transaction"
      isEditMode={isEditMode}
      formTitle={"Good Receiving" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      crudTitle={"Good Receiving" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      baseRoute="/goodRecieving"
      formPanel={formPanel}
      transactionDetailsColumns={detailColumns}
      transactionDetailsRows={transactionDetails}
      onAddDetail={handleAddDetail}
      onFormSave={handleFormSave}
      showSubmit={!formFields.IsSubmit && checkFeatureAuth("POST", "GoodRecieving", { showErrorMessage: false })}
      formLoading={formLoading}
      submitFormLoading={formLoading}
      onCancelForm={() => navigate("/goodRecieving")}
      addDetailEntryPanel={addDetailEntryPanel}
      showSave={!formFields.IsSubmit}
      showAddDetailButton={!formFields.IsSubmit}
    />
  );
};

export default CreateEdit;
